import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Mic, Square, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { cloneVoice, errMsg } from '../../lib/aiStudioApi';
import { cn } from '../../lib/utils';

const SCRIPT = 'The city lights flickered as the rain began to fall. I remember that night clearly — the laughter, the music, the promise we made. Some stories begin quietly, and this one began with a single, brave choice.';

export function OwnVoiceDialog({ open, onOpenChange, onCreated }) {
  const [tab, setTab] = useState('record');
  const [name, setName] = useState('My voice');
  const [language, setLanguage] = useState('en');
  const [gender, setGender] = useState('neutral');
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [saving, setSaving] = useState(false);
  const recRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => { clearInterval(timerRef.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setFile(new File([new Blob(chunks, { type: 'audio/webm' })], 'my-voice.webm', { type: 'audio/webm' }));
      };
      rec.start();
      recRef.current = rec;
      setSeconds(0); setRecording(true); setFile(null);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch { toast.error('Microphone access was blocked — allow it in your browser or upload a file instead'); }
  }
  function stop() { recRef.current?.stop(); clearInterval(timerRef.current); setRecording(false); }

  async function save() {
    if (!file) { toast.error('Record or upload a voice sample first'); return; }
    if (!consent) { toast.error('Please confirm you have the right to use this voice'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name.trim() || 'My voice'); fd.append('language', language); fd.append('gender', gender); fd.append('file', file);
      await cloneVoice(fd);
      toast.success('Voice uploaded — cloning starts now, it will show Ready shortly');
      onCreated(); onOpenChange(false); setFile(null); setConsent(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="studio-dark max-w-lg border-neutral-800 bg-neutral-950 text-neutral-100" data-testid="own-voice-dialog">
        <DialogHeader><DialogTitle>Use my own voice</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          {[['record', 'Record now', Mic], ['upload', 'Upload a file', Upload]].map(([id, label, Icon]) => (
            <button key={id} type="button" onClick={() => setTab(id)} data-testid={`own-voice-tab-${id}`}
              className={cn('flex flex-1 items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs',
                tab === id ? 'border-amber-400 bg-amber-500/10 text-amber-100' : 'border-neutral-800 text-neutral-400')}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
        {tab === 'record' ? (
          <div className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500">Read this aloud (aim for 20–60 s)</div>
            <p className="text-sm leading-relaxed text-neutral-200">{SCRIPT}</p>
            <div className="flex items-center gap-3">
              {recording ? (
                <Button onClick={stop} className="bg-red-500 text-white hover:bg-red-400" data-testid="own-voice-stop"><Square size={13} className="mr-1" /> Stop</Button>
              ) : (
                <Button onClick={start} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="own-voice-record"><Mic size={13} className="mr-1" /> {file ? 'Re-record' : 'Start recording'}</Button>
              )}
              <span className={cn('font-mono text-sm', recording ? 'text-red-300' : 'text-neutral-500')}>
                {recording && <span className="mr-1 inline-block h-2 w-2 animate-pulse rounded-full bg-red-500" />}
                {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
              </span>
            </div>
            {file && !recording && <audio controls src={URL.createObjectURL(file)} className="h-8 w-full" data-testid="own-voice-playback" />}
          </div>
        ) : (
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed border-neutral-700 p-6 text-xs text-neutral-400 hover:border-amber-400/50">
            <Upload size={18} />
            {file ? file.name : 'WAV, MP3, M4A, OGG, WEBM, MP4 or MOV — clean speech, no music'}
            <input type="file" accept="audio/*,video/mp4,video/quicktime" className="hidden" data-testid="own-voice-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        )}
        <div className="grid grid-cols-3 gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Voice name" className="col-span-3 border-neutral-800 bg-neutral-900 text-sm" data-testid="own-voice-name" />
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className="col-span-2 rounded-md border border-neutral-800 bg-neutral-900 px-2 text-xs" data-testid="own-voice-language">
            {[['en', 'English'], ['fil', 'Filipino'], ['es', 'Spanish'], ['ar', 'Arabic'], ['zh', 'Chinese'], ['ja', 'Japanese'], ['ko', 'Korean'], ['hi', 'Hindi'], ['pt', 'Portuguese'], ['fr', 'French'], ['de', 'German'], ['id', 'Indonesian'], ['tr', 'Turkish']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={gender} onChange={(e) => setGender(e.target.value)} className="rounded-md border border-neutral-800 bg-neutral-900 px-2 text-xs" data-testid="own-voice-gender">
            <option value="neutral">Neutral</option><option value="female">Female</option><option value="male">Male</option>
          </select>
        </div>
        <label className="flex items-start gap-2 text-xs text-neutral-400">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="own-voice-consent" className="mt-0.5" />
          This is my own voice, or I have the speaker's written permission to clone it.
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || recording} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="own-voice-save">
            {saving ? 'Uploading…' : 'Clone my voice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
